import React, { useEffect, useState } from "react";
import Header from "../common/Header";
import { Banner, BannerItem, Button, Modal } from "@athena/forge";
import "./PrivacySettings.css";
import Footer from "../common/Footer";
import ScopeSelection, { AppScopeMapping } from "./ScopeSelection";
import {
  getUserGrants,
  deleteUserGrants,
  deleteAllUserGrants,
} from "../../api/Api";
import MutedText from "../common/MutedText";
import { useTranslation } from "react-i18next";
import {
  logLandingEvent,
  logResetAllCancelButtonClick,
  logResetAllConfirmButtonClick,
  logResetAllPrivacySettingsInitiated,
  logSaveButtonClick,
} from "../../lib/amplitude-utils";

// Consider refactoring the error banner to a common component.
const ErrorBanner = ({
  errorType,
}: {
  errorType: string;
}): React.JSX.Element => {
  const { t } = useTranslation();
  const ErrorInfo: Record<string, string> = {
    getError: t("privacysettings.errorbanner.geterror"),
    deleteError: t("privacysettings.errorbanner.deleteerror"),
    deleteAllError: t("privacysettings.errorbanner.deleteallerror"),
  };
  return (
    <Banner alertType="attention" className="fe_u_margin--bottom-medium">
      <BannerItem headingTag="h3" headerText={ErrorInfo[errorType]}>
        <MutedText
          className="uiam-header-description fe_u_font-size--small fe_u_text-align--left"
          text={t("privacysettings.errorbanner.retryattention")}
        />
      </BannerItem>
    </Banner>
  );
};

const PrivacySettings = (): React.JSX.Element => {
  const { t } = useTranslation();
  const footerLinks = [
    {
      text: t("footer.button.backtoprofile"),
      href: "/",
    },
  ];

  const [userGrantedGrants, setUserGrantedGrants] = useState<AppScopeMapping>(
    {},
  );
  const [userDeniedGrants, setUserDeniedGrants] = useState<AppScopeMapping>({});

  const footerButtons = [
    {
      id: "1",
      button: (
        <Button
          text={t("footer.button.savechanges")}
          variant="primary"
          className="fe_u_margin--bottom-small privacy-settings-button"
          onClick={async () => {
            logSaveButtonClick({ pageName: "PrivacySettings" });
            try {
              await deleteUserGrants({ grantIds: toUpdateGrantIds });
              setToUpdateGrantIds([]);
            } catch (error) {
              setErrorType("deleteError");
            }

            try {
              await fetchData();
            } catch (error) {
              setErrorType("getError");
            }
          }}
          aria-label="Save Changes"
          disabled={
            Object.keys(userGrantedGrants).length === 0 &&
            Object.keys(userDeniedGrants).length === 0
          }
        />
      ),
    },
    {
      id: "2",
      button: (
        <Button
          text={t("footer.button.discardchanges")}
          variant="secondary"
          className="fe_u_width--full privacy-settings-button"
          onClick={async () => {
            setToUpdateGrantIds([]);
          }}
          aria-label="Discard Changes"
          disabled={
            Object.keys(userGrantedGrants).length === 0 &&
            Object.keys(userDeniedGrants).length === 0
          }
        />
      ),
    },
  ];

  const groupByClientName = (
    data: {
      id: string;
      clientId: string;
      scopeName: string;
      clientName: string;
      grantStatus: string;
    }[],
  ): AppScopeMapping => {
    const result: AppScopeMapping = {};

    data.forEach((item) => {
      const clientName = item.clientName;
      if (!result[clientName]) {
        result[clientName] = [];
      }
      result[clientName].push({
        scopeName: item.scopeName,
        id: item.id,
        grantStatus: item.grantStatus,
        clientId: item.clientId,
        clientName: item.clientName,
      });
    });

    return result;
  };

  const [errorType, setErrorType] = useState<string>("");

  const [toUpdateGrantIds, setToUpdateGrantIds] = useState<string[]>([]);
  const addToUpdateGrantIds = (id: string): void => {
    if (toUpdateGrantIds.includes(id)) {
      setToUpdateGrantIds(toUpdateGrantIds.filter((i) => i !== id));
    } else {
      setToUpdateGrantIds([...toUpdateGrantIds, id]);
    }
  };

  const fetchData = async (): Promise<void> => {
    setErrorType("");
    const userGrants = await getUserGrants();
    if (userGrants && userGrants.length > 0) {
      const activeGrantItems = userGrants
        .filter((userGrant) => userGrant.status === "ACTIVE")
        .map((userGrant) => ({
          clientId: userGrant.clientId,
          id: userGrant.id,
          clientName: userGrant._links.client.title,
          scopeName: userGrant._links.scope.title || `Unknown scope (id: ${userGrant.scopeId})`,
          grantStatus: userGrant.status,
        }));

      const skippedGrantItems = userGrants
        .filter((userGrant) => userGrant.status === "SKIPPED")
        .map((userGrant) => ({
          clientId: userGrant.clientId,
          id: userGrant.id,
          clientName: userGrant._links.client.title,
          scopeName: userGrant._links.scope.title || `Unknown scope (id: ${userGrant.scopeId})`,
          grantStatus: userGrant.status,
        }));

      activeGrantItems.sort((a, b) => a.clientName.localeCompare(b.clientName));
      skippedGrantItems.sort((a, b) =>
        a.clientName.localeCompare(b.clientName),
      );

      setUserGrantedGrants(groupByClientName(activeGrantItems));
      setUserDeniedGrants(groupByClientName(skippedGrantItems));
    }
  };

  useEffect(() => {
    fetchData().catch(() => {
      setErrorType("getError");
    });
    // eslint-disable-next-line
  }, []);

  logLandingEvent({ pageName: "PrivacySettings" });
  const [showResetAllModal, setShowResetAllModal] = useState<boolean>(false);

  return (
    <>
      {errorType && <ErrorBanner errorType={errorType} />}
      <Header
        headerText={t("userprofile.pagelabel.privacysettings")}
        descriptionTextAlign="left"
        description={[
          t("privacysettings.errorbanner.descriptionlineone"),
          t("privacysettings.errorbanner.descriptionlinetwo"),
        ]}
        fontSize="large"
      />

      {((userGrantedGrants && Object.keys(userGrantedGrants).length > 0) ||
        (userDeniedGrants && Object.keys(userDeniedGrants).length > 0)) && (
        <div className="fe_u_text-align--center fe_u_margin--bottom-large">
          <Button
            text={t("privacysettings.button.resetallpermissions")}
            variant="tertiary"
            onClick={async () => {
              logResetAllPrivacySettingsInitiated();
              setShowResetAllModal(true);
            }}
            aria-label="Reset All Permissions"
          />
          <Modal
            show={showResetAllModal}
            headerText={t(
              "privacysettings.resetallpermissions.confirmationmodal.headertext",
            )}
            disableClose={true}
            alertType="attention"
            primaryButtonText={t(
              "privacysettings.resetallpermissions.confirmationmodal.primarybutton",
            )}
            hideDividers={true}
            footerSecondaryButtons={[
              {
                text: t(
                  "privacysettings.resetallpermissions.confirmationmodal.secondarybutton",
                ),
                onClick: () => {
                  logResetAllCancelButtonClick();
                  setShowResetAllModal(false);
                },
              },
            ]}
            onPrimaryClick={async () => {
              try {
                await deleteAllUserGrants();
                setToUpdateGrantIds([]);
                setUserGrantedGrants({});
                setUserDeniedGrants({});
              } catch (error) {
                setErrorType("deleteAllError");
              }

              logResetAllConfirmButtonClick();
              setShowResetAllModal(false);

              try {
                await fetchData();
              } catch (error) {
                setErrorType("getError");
              }
            }}
          />
        </div>
      )}

      <ScopeSelection
        title={t("privacysettings.scopeselection.granttitle")}
        description={t("privacysettings.scopeselection.grantdescription")}
        emptyText={t("privacysettings.scopeselection.grantemptytext")}
        accordioHeading={{
          show: t("privacysettings.scopeselection.grantshowaccordioheading"),
          hide: t("privacysettings.scopeselection.granthideaccordioheading"),
        }}
        appScopeMapping={userGrantedGrants}
        toUpdateGrantIds={toUpdateGrantIds}
        addToUpdateGrantIds={addToUpdateGrantIds}
      />

      <ScopeSelection
        title={t("privacysettings.scopeselection.deniedtitle")}
        description={t("privacysettings.scopeselection.denieddescription")}
        emptyText={t("privacysettings.scopeselection.deniedemptytext")}
        accordioHeading={{
          show: t("privacysettings.scopeselection.deniedshowaccordioheading"),
          hide: t(
            "privacysettings.scopeselection.deniedgranthideaccordioheading",
          ),
        }}
        appScopeMapping={userDeniedGrants}
        toUpdateGrantIds={toUpdateGrantIds}
        addToUpdateGrantIds={addToUpdateGrantIds}
      />

      <Footer footerLinks={footerLinks} footerButtons={footerButtons} />
    </>
  );
};

export default PrivacySettings;
